import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ExternalLink, MessageSquare, Settings, Tag, Wand2, Workflow } from "lucide-react";
import HelpdeskSuiteLayout from "../../../layouts/helpdeskLayout/HelpdeskSuiteLayout";
import { PageHeader } from "../../../components/ui/PageHeader";
import { SectionCard } from "../../../components/ui/SectionCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/Tabs";
import { useUrlState } from "../../../hooks/useUrlState";
import PortalChannelToggle from "./PortalChannelToggle";
import LabelsSettings from "./workflow/LabelsSettings";
import MacrosSettings from "./workflow/MacrosSettings";
import AutomationSettings from "./workflow/AutomationSettings";

// Configuración de canales de la mesa de ayuda.
//
// La vinculación de números (WhatsApp, Messenger, Instagram…) NO vive acá: se
// hace UNA vez en Configuración → Integraciones → Canales de comunicación, que
// es donde está el estado de cada conexión (QR, sesiones, verificación). Esta
// página solo muestra el estado del canal propio de la mesa de ayuda (el widget
// del portal) y manda a vinculaciones para el resto, en vez de duplicar el
// mismo gestor en dos lugares y que los dos se desincronicen.
// Vive en /admin/helpdesk/settings y se activa con el ítem "Canales" del rail.
//
// Además de los canales, la página reúne las herramientas de trabajo del
// equipo: etiquetas, macros y reglas de automatización, cada una en su pestaña
// (?tab=etiquetas|macros|automatizacion; sin parámetro, canales — así los links
// viejos siguen entrando donde mismo). Mismo patrón de pestañas en la URL que
// el Asistente IA: con push, para que atrás/adelante vuelva a la pestaña.

const TAB_CHANNELS = "canales";
const TAB_LABELS = "etiquetas";
const TAB_MACROS = "macros";
const TAB_AUTOMATION = "automatizacion";
const TABS = [TAB_CHANNELS, TAB_LABELS, TAB_MACROS, TAB_AUTOMATION];

export default function HelpdeskSettings() {
  const { t } = useTranslation();
  const [tab, setTab] = useUrlState("tab", TAB_CHANNELS, { push: true });
  // Un ?tab= desconocido cae a canales en vez de dejar el panel en blanco.
  const activeTab = TABS.includes(tab) ? tab : TAB_CHANNELS;

  return (
    <HelpdeskSuiteLayout activeKey="suite.settings">
      <div className="flex flex-col h-full min-h-0 bg-bg">
        {/* ── Contenido scrolleable ──────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {/* Arbitrary values on purpose: `px-5`/`py-5` are also Bootstrap
              utilities with !important (3rem), which left 43px gutters on a
              390px phone. Desktop keeps the same 3rem it rendered before. */}
          <div className="max-w-5xl mx-auto px-[1rem] py-[1.25rem] sm:px-[3rem] sm:py-[3rem]">

            {/* ── Header de página ──────────────────────────────── */}
            <PageHeader
              icon={
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
                  <Settings className="w-5 h-5" aria-hidden="true" />
                </span>
              }
              title={t("Ajustes de la mesa de ayuda")}
              subtitle={t("Canales, etiquetas, macros y reglas de automatización del equipo.")}
              className="mb-5"
            />

            <Tabs value={activeTab} onValueChange={setTab}>
              <TabsList ariaLabel={t("Secciones de los ajustes de la mesa de ayuda")}>
                <TabsTrigger value={TAB_CHANNELS} icon={<MessageSquare aria-hidden="true" />}>
                  {t("Canales")}
                </TabsTrigger>
                <TabsTrigger value={TAB_LABELS} icon={<Tag aria-hidden="true" />}>
                  {t("Etiquetas")}
                </TabsTrigger>
                <TabsTrigger value={TAB_MACROS} icon={<Wand2 aria-hidden="true" />}>
                  {t("Macros")}
                </TabsTrigger>
                <TabsTrigger value={TAB_AUTOMATION} icon={<Workflow aria-hidden="true" />}>
                  {t("Automatización")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value={TAB_CHANNELS} className="pt-5">
                <p className="mb-4 text-sm text-fg-muted">
                  {t("Canales disponibles para la mesa de ayuda.")}
                </p>

                {/* Atajo a la única pantalla donde se vinculan números: el mismo
                    texto y el mismo destino para cualquiera que llegue buscando
                    "conectar WhatsApp" desde acá. */}
                <SectionCard
                  icon={<MessageSquare className="w-4 h-4" aria-hidden="true" />}
                  title={t("Los canales se vinculan en Integraciones")}
                  bodyClassName="space-y-3"
                >
                  <p className="text-[12px] md:text-[11px] text-fg-muted">
                    {t(
                      "WhatsApp, Messenger, Instagram y demás números se conectan una sola vez en Configuración → Integraciones → Canales de comunicación. Desde ahí cada número puede usarse para la mesa de ayuda, para notificaciones o para ambos.",
                    )}
                  </p>
                  {/* Mismo botón que el enlace a la documentación de Integraciones:
                      es la acción secundaria estándar de esa pantalla. */}
                  <Link
                    to="/admin/settings?tab=integrations&sub=channels"
                    className="inline-flex items-center justify-center gap-2 h-8 px-3 text-sm rounded-md font-medium border border-border bg-transparent text-fg no-underline hover:bg-bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                  >
                    <ExternalLink size={14} aria-hidden="true" />
                    {t("Ir a vinculaciones")}
                  </Link>
                </SectionCard>

                {/* Canal del portal: no es un número que se vincule, es el widget
                    que ya vive dentro del portal de clientes, así que se queda acá
                    con su propia card. */}
                <div className="mt-5">
                  <PortalChannelToggle />
                </div>
              </TabsContent>

              <TabsContent value={TAB_LABELS} className="pt-5">
                <LabelsSettings />
              </TabsContent>
              <TabsContent value={TAB_MACROS} className="pt-5">
                <MacrosSettings />
              </TabsContent>
              <TabsContent value={TAB_AUTOMATION} className="pt-5">
                <AutomationSettings />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </HelpdeskSuiteLayout>
  );
}
